import React, { useState, useMemo } from 'react';
import moment from 'moment';
import './LocationReport.css';

const LocationReport = ({ isOpen, onClose, events, resources }) => {
    // If the modal isn't open, don't render anything

    // Track which view mode is selected: "daily", "weekly", or "monthly"
    const [viewMode, setViewMode] = useState('daily');

    // We'll keep a single date in state; how we interpret it depends on the view
    // Default is the current day in YYYY-MM-DD format
    const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));

    // NEW: Track which user is selected ("" means "All Users")
    const [selectedUser, setSelectedUser] = useState('');

    // -------------------------------------
    // 1) Filter events by selected user
    // -------------------------------------
    const userFilteredEvents = useMemo(() => {
        // If selectedUser is empty, we keep all events
        if (!selectedUser) return events;
        // Otherwise, keep only events whose `resourceId` matches
        return events.filter((evt) => evt.resourceId === selectedUser);
    }, [events, selectedUser]);

    // -------------------------------------
    // 2) Filter by daily/weekly/monthly
    // -------------------------------------
    const viewFilteredEvents = useMemo(() => {
        const baseDate = moment(selectedDate, 'YYYY-MM-DD', true); // parse strictly
        if (!baseDate.isValid()) return [];

        if (viewMode === 'daily') {
            // Keep only events matching that single day
            return userFilteredEvents.filter(evt =>
                moment(evt.start).isSame(baseDate, 'day')
            );
        }
        else if (viewMode === 'weekly') {
            // We'll define the "week" to run Saturday -> Friday
            // Find the "Saturday" that starts the week containing `baseDate`.
            const dayOfWeek = baseDate.day(); // Sunday=0 ... Saturday=6
            let startOfWeek = baseDate.clone().day(6);
            if (startOfWeek.isAfter(baseDate)) {
                startOfWeek = startOfWeek.subtract(7, 'days');
            }
            const endOfWeek = startOfWeek.clone().add(6, 'days').endOf('day');
            return userFilteredEvents.filter(evt =>
                moment(evt.start).isBetween(startOfWeek, endOfWeek, null, '[]')
            );
        }
        else if (viewMode === 'monthly') {
            // Keep events in that calendar month
            const startOfMonth = baseDate.clone().startOf('month');
            const endOfMonth = baseDate.clone().endOf('month');
            return userFilteredEvents.filter(evt =>
                moment(evt.start).isBetween(startOfMonth, endOfMonth, null, '[]')
            );
        }

        return userFilteredEvents;
    }, [userFilteredEvents, viewMode, selectedDate]);

    // -------------------------------------
    // 3) Summarize usage by location
    // -------------------------------------
    const usageSummary = useMemo(() => {
        const summary = {};
        viewFilteredEvents.forEach(evt => {
            const loc = evt.location || 'Unknown';
            // Calculate duration in hours
            const duration = moment(evt.end).diff(moment(evt.start), 'hours', true);

            if (!summary[loc]) summary[loc] = 0;
            summary[loc] += duration;
        });
        return summary;
    }, [viewFilteredEvents]);

    // Helper: nicely formatted heading for the chosen timeframe
    const getHeading = () => {
        if (viewMode === 'daily') {
            return `Day: ${moment(selectedDate).format('YYYY-MM-DD')}`;
        } else if (viewMode === 'weekly') {
            return `Week of: ${moment(selectedDate).format('YYYY-MM-DD')} (Sat-Fri)`;
        } else if (viewMode === 'monthly') {
            return `Month of: ${moment(selectedDate).format('YYYY-MM')}`;
        }
        return '';
    };

    // Helper: get the user’s name if selected
    const userTitle = resources.find((r) => r.id === selectedUser)?.title || 'All Users';

    return (
        <div className={`location-report-modal ${isOpen ? 'active' : ''}`} onClick={onClose}>
            <div className="location-report-content" onClick={(e) => e.stopPropagation()}>
                {/* Close Button */}
                <button className="location-report-close-btn" onClick={onClose}>X</button>
            <h2>Location Report</h2>

            {/* Controls: pick daily/weekly/monthly, pick date */}
            <div className="lr-controls">
                {/* View Selector - Label on top, select below */}
                <div className="lr-control-group">
                    <label>View:</label>
                    <select value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                    </select>
                </div>

                {/* Date Selector - Label inline, input below */}
                {(viewMode === 'daily' || viewMode === 'weekly') && (
                    <div className="lr-date-group">
                        <label>Select Date:</label>
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                        />
                        {viewMode === 'weekly' && <p className="lr-hint">Week runs Saturday to Friday.</p>}
                    </div>
                )}

                {/* Month Selector - Label inline, input below */}
                {viewMode === 'monthly' && (
                    <div className="lr-date-group">
                        <label>Select Month:</label>
                        <input
                            type="month"
                            value={moment(selectedDate).format('YYYY-MM')}
                            onChange={(e) => {
                                const [year, month] = e.target.value.split('-');
                                const newDate = moment().year(year).month(month - 1).date(1).format('YYYY-MM-DD');
                                setSelectedDate(newDate);
                            }}
                        />
                    </div>
                )}

                {/* User Selector - Label inline, select below */}
                <div className="lr-user-group">
                    <label>Select User:</label>
                    <select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
                        <option value="">All Users</option>
                        {resources.map((user) => (
                            <option key={user.id} value={user.id}>
                                {user.title}
                            </option>
                        ))}
                    </select>
                </div>
            </div>


            {/* Summaries */}
            <h3 className="lr-chosen-heading">{getHeading()}</h3>
            <p style={{ marginBottom: '1rem' }}>
                User: <strong>{userTitle}</strong>
            </p>

            {/* Simple table: Location + total hours */}
            <div className="lr-table-container">
                <table>
                    <thead>
                        <tr>
                            <th>Location</th>
                            <th>Total Hours</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.keys(usageSummary).length === 0 && (
                            <tr>
                                <td colSpan="2" style={{ textAlign: 'center', fontStyle: 'italic' }}>
                                    No data for this {viewMode}.
                                </td>
                            </tr>
                        )}

                        {Object.keys(usageSummary).map((loc) => (
                            <tr key={loc}>
                                <td>{loc}</td>
                                <td>{usageSummary[loc].toFixed(2)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

        </div>
        </div >
    );
};

export default LocationReport;
